import React, { useState, useEffect } from "react";
import Iconify from "../../../../../Components/Dashboard/Iconify";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import LoadingOverlay from "react-loading-overlay-ts";

// MUI Imports
import {
  Box,
  Button,
  CircularProgress,
  ThemeProvider,
  Grid,
  Container,
  Typography,
  Switch,
} from "@mui/material";

// Project Imports
import Page from "../../../../../Components/Dashboard/Page";
import { poppinsCustomTheme } from "../../../../../Styles/GlobalStyles/PoppinsTheme";

import { getSystemSettings, saveSystemSettings } from "../../../../../Actions";

export default function SystemSettings() {
  const dispatch = useDispatch();
  const settingsState = (state) => state.systemSettings;
  const { fetchingSystemSettings, systemSettings, savingSystemSettings } =
    useSelector(settingsState);

  const [settings, setSettings] = useState({});
  const [updateMade, setUpdateMade] = useState(false);

  function handleSettingChange(settingName, newValue) {
    const newSettings = {
      ...settings,
      [`${settingName}`]: newValue,
    };
    setSettings(newSettings);

    const changeMade = !_.isEqual(newSettings, systemSettings);
    setUpdateMade(changeMade);
  }

  function handleSaveSettings() {
    dispatch(saveSystemSettings(settings));
  }

  const settingsMarkup = [
    {
      icon: "la:user-cog",
      title: "Trainer's Registration",
      subTitle: "Open or close registration for training centers",
      action: {
        type: "switch",
        checked: settings?.isTrainingCenterRegistrationActive,
        label: settings?.isTrainingCenterRegistrationActive
          ? "Active"
          : "Inactive",
        onChange: (event) =>
          handleSettingChange(
            "isTrainingCenterRegistrationActive",
            event.target.checked
          ),
      },
    },
  ];

  useEffect(() => {
    dispatch(getSystemSettings());
  }, [dispatch]);

  useEffect(() => {
    if (Object.keys(systemSettings).length > 0) {
      setSettings(systemSettings);
      const changeMade = !_.isEqual(settings, systemSettings);
      setUpdateMade(changeMade);
    }

    // ignore eslint error on the next line
    // We are deliberately not watching for settings.
    // If we do so, the system won't behave correctly.
  }, [systemSettings]);

  return (
    <ThemeProvider theme={poppinsCustomTheme}>
      <Page title="SAED | Settings">
        <Page>
          <Container maxWidth="xl">
            <Box
              sx={{
                pb: 2,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h5"
                style={{ fontWeight: 600, color: "#212B36" }}
              >
                Site Settings
              </Typography>
              {updateMade && (
                <Button
                  disabled={savingSystemSettings}
                  onClick={handleSaveSettings}
                >
                  {savingSystemSettings ? <>Saving</> : <>Save Changes</>}
                </Button>
              )}
            </Box>

            <LoadingOverlay
              active={fetchingSystemSettings || savingSystemSettings}
              styles={{
                overlay: (base) => ({
                  ...base,
                  background: "rgba(0, 0, 0, 0.15)",
                }),
              }}
              spinner
              text="Loading..."
            >
              <Grid
                container
                spacing={3}
                style={{
                  paddingTop: 24,
                  paddingLeft: 24,
                  display: "flex",
                }}
              >
                {/* settings-section */}

                {settingsMarkup.map((setting) => {
                  return <SettingAtom {...setting} />;
                })}
              </Grid>
            </LoadingOverlay>
          </Container>
        </Page>
      </Page>
    </ThemeProvider>
  );
}

const GetIcon = ({ name }) => <Iconify icon={name} width={26} height={26} />;

function SettingAtom({ icon, title, subTitle, action }) {
  return (
    <Grid
      item
      xs
      container
      style={{
        backgroundColor: "white",
        padding: 16,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid #d3d3d3a1",
      }}
    >
      {/* header */}
      <Grid
        item
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <GetIcon name={icon} />
        <Grid
          style={{
            display: "flex",
            flexDirection: "column",
            marginLeft: 16,
          }}
        >
          <Typography variant="body1">{title}</Typography>
          <Typography variant="caption" sx={{ color: "#00000087" }}>
            {subTitle}
          </Typography>
        </Grid>
      </Grid>

      {/* actions */}
      <Grid
        item
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Typography>{action.label}</Typography>
        <Switch checked={action.checked} onChange={action.onChange} />
      </Grid>
    </Grid>
  );
}
